import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DASHBOARD_STATES } from '../constants';
import { getDuplicateBaseTitle } from '../utils/dashboardDisplayTitle';
import { sortDashboards } from '../utils/dashboardList';
import apiDashboardDataAdapters from './apiDataAdapters';

const DashboardDataContext = createContext(null);

const upsertDashboardInCollection = (items = [], dashboard) => {
  if (!dashboard?.id) return sortDashboards(items);
  const exists = items.some((item) => item.id === dashboard.id);
  const nextItems = exists
    ? items.map((item) => (item.id === dashboard.id ? { ...item, ...dashboard } : item))
    : [...items, dashboard];
  return sortDashboards(nextItems);
};

const duplicateWidgetsForDashboard = (widgets = [], dashboardId) =>
  widgets.map((widget, index) => {
    const instanceId = `${dashboardId}_${widget?.widgetType || 'widget'}_${index}`;
    return {
      ...widget,
      instanceId,
      layout: {
        ...(widget?.layout || {}),
        i: instanceId,
      },
    };
  });

/**
 * Data provider for dashboards domain.
 *
 * Defaults to the API-backed adapters (`/v0/dashboard/dashboard`); `dataAdapters`
 * stays injectable so tests and Storybook can hand it a stub.
 */
export const DashboardDataProvider = ({ children, dataAdapters }) => {
  const adapters = useMemo(
    () => dataAdapters ?? apiDashboardDataAdapters,
    [dataAdapters],
  );

  const [state, setState] = useState({
    dashboards: [],
    templates: [],
    templateCategories: [],
    availableWidgets: [],
    shareCandidates: [],
    sections: [],
    sectionFilters: [],
    loadingDashboards: false,
    loadingTemplates: false,
    loadingTemplateCategories: false,
    loadingAvailableWidgets: false,
    loadingShareCandidates: false,
    loadingSections: false,
    loadingSectionFilters: false,
    error: null,
  });

  const sectionsLoadedRef = useRef(false);
  const sectionFiltersLoadedRef = useRef(false);
  const categoriesLoadedRef = useRef(false);
  const mountedRef = useRef(true);
  const dashboardsRequestIdRef = useRef(0);
  const templatesRequestIdRef = useRef(0);
  const sectionsRequestIdRef = useRef(0);
  const sectionFiltersRequestIdRef = useRef(0);
  const categoriesRequestIdRef = useRef(0);
  const widgetsRequestIdRef = useRef(0);
  const shareRequestIdRef = useRef(0);

  useEffect(
    () => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        dashboardsRequestIdRef.current += 1;
        templatesRequestIdRef.current += 1;
        sectionsRequestIdRef.current += 1;
        sectionFiltersRequestIdRef.current += 1;
        categoriesRequestIdRef.current += 1;
        widgetsRequestIdRef.current += 1;
        shareRequestIdRef.current += 1;
      };
    },
    [],
  );

  const updateState = useCallback((updates) => {
    if (!mountedRef.current) return;
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const fetchDashboards = useCallback(
    async (filters = {}) => {
      const requestId = ++dashboardsRequestIdRef.current;
      updateState({ loadingDashboards: true, error: null });
      try {
        const dashboards = await adapters.fetchDashboards(filters);
        if (!mountedRef.current || requestId !== dashboardsRequestIdRef.current) return;
        updateState({
          dashboards: sortDashboards(Array.isArray(dashboards) ? dashboards : []),
          loadingDashboards: false,
        });
      } catch (error) {
        if (!mountedRef.current || requestId !== dashboardsRequestIdRef.current) return;
        updateState({
          dashboards: [],
          loadingDashboards: false,
          error,
        });
      }
    },
    [adapters, updateState],
  );

  const fetchTemplates = useCallback(
    async (filters = {}) => {
      const requestId = ++templatesRequestIdRef.current;
      updateState({ loadingTemplates: true, error: null });
      try {
        const templates = await adapters.fetchTemplates(filters);
        if (!mountedRef.current || requestId !== templatesRequestIdRef.current) return;
        updateState({
          templates: Array.isArray(templates) ? templates : [],
          loadingTemplates: false,
        });
      } catch (error) {
        if (!mountedRef.current || requestId !== templatesRequestIdRef.current) return;
        updateState({
          templates: [],
          loadingTemplates: false,
          error,
        });
      }
    },
    [adapters, updateState],
  );

  const fetchSections = useCallback(async () => {
    if (sectionsLoadedRef.current) return;
    sectionsLoadedRef.current = true;
    const requestId = ++sectionsRequestIdRef.current;
    updateState({ loadingSections: true, error: null });
    try {
      const sections = await adapters.fetchSections();
      if (!mountedRef.current || requestId !== sectionsRequestIdRef.current) return;
      updateState({
        sections: Array.isArray(sections) ? sections : [],
        loadingSections: false,
      });
    } catch (error) {
      if (!mountedRef.current || requestId !== sectionsRequestIdRef.current) return;
      sectionsLoadedRef.current = false;
      updateState({
        sections: [],
        loadingSections: false,
        error,
      });
    }
  }, [adapters, updateState]);

  const fetchSectionFilters = useCallback(async () => {
    if (sectionFiltersLoadedRef.current) return;
    sectionFiltersLoadedRef.current = true;
    const requestId = ++sectionFiltersRequestIdRef.current;
    updateState({ loadingSectionFilters: true, error: null });
    try {
      const sectionFilters = await adapters.fetchSectionFilters();
      if (!mountedRef.current || requestId !== sectionFiltersRequestIdRef.current) return;
      updateState({
        sectionFilters: Array.isArray(sectionFilters) ? sectionFilters : [],
        loadingSectionFilters: false,
      });
    } catch (error) {
      if (!mountedRef.current || requestId !== sectionFiltersRequestIdRef.current) return;
      sectionFiltersLoadedRef.current = false;
      updateState({
        sectionFilters: [],
        loadingSectionFilters: false,
        error,
      });
    }
  }, [adapters, updateState]);

  const fetchTemplateCategories = useCallback(async () => {
    if (categoriesLoadedRef.current) return;
    categoriesLoadedRef.current = true;
    const requestId = ++categoriesRequestIdRef.current;
    updateState({ loadingTemplateCategories: true, error: null });
    try {
      const categories = await adapters.fetchTemplateCategories();
      if (!mountedRef.current || requestId !== categoriesRequestIdRef.current) return;
      updateState({
        templateCategories: Array.isArray(categories) ? categories : [],
        loadingTemplateCategories: false,
      });
    } catch (error) {
      if (!mountedRef.current || requestId !== categoriesRequestIdRef.current) return;
      categoriesLoadedRef.current = false;
      updateState({
        templateCategories: [],
        loadingTemplateCategories: false,
        error,
      });
    }
  }, [adapters, updateState]);

  const fetchAvailableWidgets = useCallback(async () => {
    const requestId = ++widgetsRequestIdRef.current;
    updateState({ loadingAvailableWidgets: true, error: null });
    try {
      const widgets = await adapters.getAvailableWidgets();
      if (!mountedRef.current || requestId !== widgetsRequestIdRef.current) return;
      updateState({
        availableWidgets: Array.isArray(widgets) ? widgets : [],
        loadingAvailableWidgets: false,
      });
    } catch (error) {
      if (!mountedRef.current || requestId !== widgetsRequestIdRef.current) return;
      updateState({
        availableWidgets: [],
        loadingAvailableWidgets: false,
        error,
      });
    }
  }, [adapters, updateState]);

  const fetchShareCandidates = useCallback(async () => {
    const requestId = ++shareRequestIdRef.current;
    updateState({ loadingShareCandidates: true, error: null });
    try {
      const candidates = await adapters.getShareCandidates();
      if (!mountedRef.current || requestId !== shareRequestIdRef.current) return;
      updateState({
        shareCandidates: Array.isArray(candidates) ? candidates : [],
        loadingShareCandidates: false,
      });
    } catch (error) {
      if (!mountedRef.current || requestId !== shareRequestIdRef.current) return;
      updateState({
        shareCandidates: [],
        loadingShareCandidates: false,
        error,
      });
    }
  }, [adapters, updateState]);

  const getDashboardById = useCallback((id) => adapters.getDashboardById(id), [adapters]);
  const getTemplateById = useCallback((id) => adapters.getTemplateById(id), [adapters]);
  const createDashboard = useCallback(
    async (payload) => {
      const created = await adapters.createDashboard(payload);
      if (mountedRef.current) {
        setState((prev) => ({
          ...prev,
          dashboards: upsertDashboardInCollection(prev.dashboards, created),
        }));
      }
      return created;
    },
    [adapters],
  );

  const updateDashboard = useCallback(
    async (id, payload) => {
      const updated = await adapters.updateDashboard(id, payload);
      if (mountedRef.current) {
        setState((prev) => ({
          ...prev,
          dashboards: upsertDashboardInCollection(prev.dashboards, updated),
        }));
      }
      return updated;
    },
    [adapters],
  );

  const publishDashboard = useCallback(
    async (id) => {
      const updated = await adapters.publishDashboard(id);
      if (mountedRef.current) {
        setState((prev) => ({
          ...prev,
          dashboards: upsertDashboardInCollection(prev.dashboards, updated),
        }));
      }
      return updated;
    },
    [adapters],
  );

  const duplicateDashboard = useCallback(
    async (id) => {
      if (typeof adapters.duplicateDashboard === 'function') {
        const duplicated = await adapters.duplicateDashboard(id);
        if (mountedRef.current) {
          setState((prev) => ({
            ...prev,
            dashboards: upsertDashboardInCollection(prev.dashboards, duplicated),
          }));
        }
        return duplicated;
      }

      const source = await adapters.getDashboardById(id);
      const baseTitle = getDuplicateBaseTitle(source);
      const draftId = `dash-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const sourceWidgets = Array.isArray(source?.widgets) ? source.widgets : [];
      const sourceRest = { ...source };
      delete sourceRest.titleKey;
      delete sourceRest.descriptionKey;
      return createDashboard({
        ...sourceRest,
        id: draftId,
        title: baseTitle,
        hasDuplicateTitleSuffix: true,
        state: DASHBOARD_STATES.DRAFT,
        archivedAt: null,
        archivedFromState: null,
        sharedWith: [],
        widgets: duplicateWidgetsForDashboard(sourceWidgets, draftId),
      });
    },
    [adapters, createDashboard],
  );

  const shareDashboard = useCallback(
    async (id, assignments) => {
      const updated = await adapters.shareDashboard(id, assignments);
      if (mountedRef.current) {
        setState((prev) => ({
          ...prev,
          dashboards: upsertDashboardInCollection(prev.dashboards, updated),
        }));
      }
      return updated;
    },
    [adapters],
  );

  const value = useMemo(
    () => ({
      ...state,
      fetchDashboards,
      fetchTemplates,
      fetchSections,
      fetchSectionFilters,
      fetchTemplateCategories,
      fetchAvailableWidgets,
      fetchShareCandidates,
      getDashboardById,
      getTemplateById,
      createDashboard,
      updateDashboard,
      publishDashboard,
      duplicateDashboard,
      shareDashboard,
    }),
    [
      state,
      fetchDashboards,
      fetchTemplates,
      fetchSections,
      fetchSectionFilters,
      fetchTemplateCategories,
      fetchAvailableWidgets,
      fetchShareCandidates,
      getDashboardById,
      getTemplateById,
      createDashboard,
      updateDashboard,
      publishDashboard,
      duplicateDashboard,
      shareDashboard,
    ],
  );

  return <DashboardDataContext.Provider value={value}>{children}</DashboardDataContext.Provider>;
};

export const useDashboardData = () => {
  const context = useContext(DashboardDataContext);
  if (!context) {
    throw new Error('useDashboardData must be used within DashboardDataProvider');
  }
  return context;
};
